import { AdaptError } from "@common/lib/error-handler/adapt-error";
import { Entity, EntityError, HttpResponse } from "breeze-client";
import { QueuedSaveFailedError } from "breeze-client/mixin-save-queuing";
import { entityChanged$ } from "breeze-client/rxjs";
import { filter, take } from "rxjs/operators";

// taken from breeze.SaveChangesErrorCallback
export interface IBreezeSaveChangesError extends Error {
    name: string;
    message: string;
    stack?: string;

    entityErrors?: EntityError[];
    httpResponse: HttpResponse;
    status: number;
}

export interface ISaveErrorMessageService {
    getErrorMessage(error: IBreezeSaveChangesError): string;
}

export class BreezeSaveError extends AdaptError implements ISaveErrorMessageService {
    public readonly saveError: IBreezeSaveChangesError;

    public constructor(
        saveError: IBreezeSaveChangesError | QueuedSaveFailedError,
    ) {
        // Save queuing wraps the error and has the concrete error in the innerError property.
        const unwrappedError = isQueuedSaveError(saveError)
            ? saveError.innerError as unknown as IBreezeSaveChangesError
            : saveError;

        const msg = unwrappedError.status < 100
            // not even a valid http response code -> guessing is the network issue
            ? "Save failed: no response from server (possibly caused by bad network connection)"
            : `Save failed: ${BreezeSaveError.getErrorMessage(unwrappedError)}`;

        super(msg);
        this.saveError = unwrappedError;
        this.name = "BreezeSaveError";
    }

    public get shouldBeLogged() {
        // Don't log failed saves with when there are entity errors - this will be displayed in the UI
        // Also don't log unauthorised errors - these are part of expected operation
        return this.saveError.status !== 401 && !this.saveError.entityErrors;
    }

    /*
     * https://github.com/Breeze/breeze.js.labs/blob/master/breeze.saveErrorExtensions.js
     *
     * Copyright 2015 IdeaBlade, Inc.  All Rights Reserved.
     * Licensed under the MIT License
     * http://opensource.org/licenses/mit-license.php
     * Author: Ward Bell
    */
    public static getErrorMessage(error: IBreezeSaveChangesError) {
        const msg = error.message;
        const entityErrors = error.entityErrors;

        if (entityErrors && entityErrors.length) {
            this.reviewServerErrors(entityErrors);
            return this.getValidationMessages(entityErrors);
        }

        return msg!;
    }

    private static getValidationMessages(entityErrors: EntityError[]) {
        const isServerError = entityErrors[0].isServerError; // if the first is, they all are
        try {
            return entityErrors.map((err) => err.errorMessage).join("; <br/>");
        } catch (_) {
            /* eat it for now */
            return (isServerError ? "server" : "client") + " validation error";
        }
    }

    private static reviewServerErrors(entityErrors: EntityError[]) {
        const entitiesWithServerErrors: Entity[] = [];
        entityErrors.forEach((entityError) => {
            const entity = entityError.isServerError && entityError.entity;
            if (entity && entitiesWithServerErrors.indexOf(entity) === -1) {
                entitiesWithServerErrors.push(entity);
                this.clearServerErrorsOnNextChange(entity);
            }
        });
    }

    private static clearServerErrorsOnNextChange(entity: Entity) {
        if (entity.entityAspect.entityState.isDetached()) { return; }

        // Clear server validation errors on the next change to this entity.
        const manager = entity.entityAspect.entityManager!;
        entityChanged$(manager).pipe(
            filter((changeArgs) => changeArgs.entity === entity),
            take(1),
        ).subscribe(() => {
            entity.entityAspect.getValidationErrors().forEach((err) => {
                if (err.isServerError) {
                    entity.entityAspect.removeValidationError(err);
                }
            });
        });
    }

    public getErrorMessage(error: IBreezeSaveChangesError) {
        return BreezeSaveError.getErrorMessage(error);
    }
}

function isQueuedSaveError(error: any): error is QueuedSaveFailedError {
    return !!error.innerError;
}
